import React, { ReactNode, useEffect } from 'react';
import { useSwiper } from '../../../../hooks/useSwiper';
import { ProgressBar } from '../../../../components';
import { SliderLeftArrow } from '../../../home/partials/Pofrtolio/SliderLeftArrow';
import { SliderRightArrow } from '../../../home/partials/Pofrtolio/SliderRightArrow';
import { COLORS, DATA_TYPE_ERROR_MESSAGE, device } from '../../../../constants';
import styled from 'styled-components';

type SwiperProps<T> = {
  data: T[];
  renderItem: (item: T, index: number, length: number) => ReactNode;
};
const AboutUsProvideSwiper = <T,>({ data, renderItem }: SwiperProps<T>) => {
  const {
    isOverflow,
    isAtStart,
    isAtEnd,
    visibleIndex,
    swiperRef,
    checkForOverflow,
    updateArrowStates,
    onTouchStart,
    onTouchMove,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    handleNextClick,
    handlePrevClick,
    barElementClick,
  } = useSwiper(data.length);

  useEffect(() => {
    checkForOverflow();
    window.addEventListener('resize', checkForOverflow);
    return () => window.removeEventListener('resize', checkForOverflow);
  }, [data, checkForOverflow]);

  useEffect(() => {
    updateArrowStates();
  }, [data, visibleIndex, updateArrowStates]);

  return (
    <>
      <AboutUsProvideSwiperWrapper>
        {Array.isArray(data) && data.length > 0 ? (
          <AboutUsProvideSwiperContainer>
            <ElementsContainer
              $isOverflow={isOverflow}
              ref={swiperRef}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onMouseUp}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseLeave}
            >
              <ElementsList>
                {data.map((item, idx) => (
                  <SlideWrapper key={idx}>
                    {renderItem(item, idx, data.length)}
                  </SlideWrapper>
                ))}
              </ElementsList>
            </ElementsContainer>

            <ArrowContainer>
              <PrevArrowContainer>
                <ArrowPrev onClick={handlePrevClick} disabled={isAtStart}>
                  <SliderLeftArrow />
                </ArrowPrev>
              </PrevArrowContainer>
              <NextArrowContainer>
                <ArrowNext onClick={handleNextClick} disabled={isAtEnd}>
                  <SliderRightArrow />
                </ArrowNext>
              </NextArrowContainer>
            </ArrowContainer>
          </AboutUsProvideSwiperContainer>
        ) : (
          <p>${DATA_TYPE_ERROR_MESSAGE}</p>
        )}
      </AboutUsProvideSwiperWrapper>
      {isOverflow && (
        <ProgressBarContainer>
          <ProgressBar
            data={data}
            visibleIndex={visibleIndex}
            barElementClick={barElementClick}
          />
        </ProgressBarContainer>
      )}
    </>
  );
};

const AboutUsProvideSwiperWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 130px;
  display: flex;
  justify-content: center;
  @media screen and (${device.sm}) {
    bottom: 70px;
  }
`;
const AboutUsProvideSwiperContainer = styled.div`
  display: flex;
  background-color: ${COLORS.GRAY_BG_COLOR};
  max-width: 450px;
  padding: 40px;
  box-sizing: border-box;
  flex-direction: column;
  @media screen and (${device.lg}) {
    max-width: 350px;
    padding: 20px;
  }
  @media screen and (${device.sm}) {
    max-width: 250px;
    padding: 10px;
  }
`;

const ElementsContainer = styled.div<{ $isOverflow: boolean }>`
  width: 100%;
  overflow: hidden;
  scroll-behavior: smooth;
  display: ${props => (!props.$isOverflow ? 'flex' : 'block')};
  justify-content: center;
  @media screen and (${device.sm}) {
    min-height: 0;
  }
  padding-bottom: 40px;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  user-select: none;
  @media screen and (${device.lg}) {
    padding-bottom: 20px;
  }
  @media screen and (${device.sm}) {
    padding-bottom: 10px;
  }
`;
const ProgressBarContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`;
const SlideWrapper = styled.div`
  width: 100%;
  flex-shrink: 0;
`;

const ElementsList = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  transition: 2s;
`;

const ArrowPrev = styled.div<{ disabled: boolean }>`
  width: 100%;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`;

const ArrowNext = styled.div<{ disabled: boolean }>`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;

  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`;
const ArrowContainer = styled.div`
  display: flex;
  gap: 10px;
`;
const PrevArrowContainer = styled.div`
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NextArrowContainer = styled.div`
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export { AboutUsProvideSwiper };
